import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CalendarCheck, CalendarDays, Clock, Plus, TrendingUp, Users, Wallet, CircleDollarSign } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { KpiCard, PageHeader } from "@/components/common/Page";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ClassFormDialog } from "@/components/classes/ClassFormDialog";
import { ClassDetailDialog } from "@/components/classes/ClassDetailDialog";
import { useFetch } from "@/hooks/useFetch";
import { brl, fmtShort, todayISO } from "@/lib/format";

const tooltipStyle = { contentStyle: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 } };
const axis = { stroke: "hsl(var(--muted-foreground))", fontSize: 11, tickLine: false, axisLine: false };

const ChartCard = ({ title, children, testId }) => (
  <div className="rounded-xl border bg-card p-5" data-testid={testId}>
    <h3 className="mb-4 font-heading font-semibold">{title}</h3>
    <div className="h-52">{children}</div>
  </div>
);

const ClassRow = ({ c, onOpen }) => (
  <button onClick={() => onOpen(c.id)} className="flex w-full items-center gap-4 rounded-lg px-3 py-3 text-left transition-colors hover:bg-muted/60" data-testid={`upcoming-class-${c.id}`}>
    <div className="w-14 shrink-0 text-center">
      <p className="font-mono text-base font-semibold text-primary">{c.start_time}</p>
      <p className="text-[11px] text-muted-foreground">{c.date === todayISO() ? "Hoje" : fmtShort(c.date)}</p>
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold">{c.subject} — {c.student_name}</p>
      <p className="text-xs text-muted-foreground">{c.duration} min · {c.type === "online" ? "Online" : "Presencial"}</p>
    </div>
    <StatusBadge status={c.status} />
  </button>
);

export default function Dashboard() {
  const { data, loading, error, reload } = useFetch("/dashboard");
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  if (loading && !data) return <LoadingState rows={5} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  const k = data.kpis;

  return (
    <div className="animate-in fade-in duration-300">
      <PageHeader title="Dashboard" subtitle="Visão geral das suas aulas e finanças"
        actions={<Button onClick={() => setNewOpen(true)} data-testid="dashboard-new-class-btn"><Plus className="mr-2 h-4 w-4" />Nova aula</Button>} />
      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <KpiCard label="Alunos ativos" value={k.alunos_ativos} icon={Users} testId="kpi-active-students" />
        <KpiCard label="Aulas hoje" value={k.aulas_hoje} icon={CalendarDays} testId="kpi-today-classes" />
        <KpiCard label="Próximas aulas" value={k.proximas_aulas} icon={Clock} testId="kpi-upcoming-classes" />
        <KpiCard label="Realizadas no mês" value={k.aulas_realizadas_mes} icon={CalendarCheck} tone="text-accent" testId="kpi-done-month" />
        <KpiCard label="Receita prevista (mês)" value={brl(k.receita_prevista)} icon={TrendingUp} testId="kpi-expected-revenue" />
        <KpiCard label="Receita recebida (mês)" value={brl(k.receita_recebida)} icon={Wallet} tone="text-accent" testId="kpi-received-revenue" />
        <KpiCard label="Pagamentos pendentes" value={brl(k.pagamentos_pendentes)} icon={CircleDollarSign} tone="text-amber-500" testId="kpi-pending-payments" />
        <KpiCard label="Alunos inadimplentes" value={k.alunos_inadimplentes} icon={AlertCircle} tone="text-destructive" testId="kpi-late-students" />
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border bg-card p-5 lg:col-span-1" data-testid="upcoming-classes-section">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-heading font-semibold">Próximas aulas</h3>
            <Link to="/agenda" className="text-xs font-medium text-primary hover:underline" data-testid="see-agenda-link">Ver agenda</Link>
          </div>
          {data.upcoming.length === 0 ? (
            <EmptyState icon={CalendarDays} title="Nenhuma aula agendada" description="Agende sua próxima aula para vê-la aqui."
              action={<Button size="sm" onClick={() => setNewOpen(true)} data-testid="empty-new-class-btn">Agendar aula</Button>} />
          ) : (
            <div className="-mx-3 space-y-1">{data.upcoming.map((c) => <ClassRow key={c.id} c={c} onOpen={setDetail} />)}</div>
          )}
        </div>
        <div className="grid min-w-0 gap-6 lg:col-span-2 lg:grid-cols-2">
          <ChartCard title="Receita mensal" testId="chart-revenue">
            <ResponsiveContainer>
              <AreaChart data={data.charts.revenue}>
                <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" {...axis} /><YAxis {...axis} width={50} />
                <Tooltip {...tooltipStyle} formatter={(v) => brl(v)} />
                <Area type="monotone" dataKey="valor" name="Receita" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Aulas realizadas por mês" testId="chart-classes">
            <ResponsiveContainer>
              <BarChart data={data.charts.classes}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" {...axis} /><YAxis {...axis} width={30} allowDecimals={false} />
                <Tooltip {...tooltipStyle} cursor={{ fill: "hsl(var(--muted))" }} />
                <Bar dataKey="aulas" name="Aulas" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <div className="lg:col-span-2">
            <ChartCard title="Evolução do número de alunos" testId="chart-students">
              <ResponsiveContainer>
                <LineChart data={data.charts.students}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" {...axis} /><YAxis {...axis} width={30} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Line type="monotone" dataKey="alunos" name="Alunos" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </div>
      </div>
      <ClassFormDialog open={newOpen} onOpenChange={setNewOpen} onSaved={reload} />
      <ClassDetailDialog open={!!detail} lessonId={detail} onOpenChange={(o) => !o && setDetail(null)} onChanged={reload} />
    </div>
  );
}
